// Necessary if using App Router to ensure this file runs on the client
'use client';

import { datadogRum } from '@datadog/browser-rum';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { Env } from '@/libs/Env';
import { getClientUser } from '@/libs/supabaseSSR/client';

const requiredEnvVars = ['NEXT_PUBLIC_DATADOG_APPLICATION_ID', 'NEXT_PUBLIC_DATADOG_CLIENT_TOKEN', 'NEXT_PUBLIC_DATADOG_SERVICE_NAME', 'NEXT_PUBLIC_DATADOG_ENV'];
const enableDatadog = requiredEnvVars.every(envVar => Env[envVar as keyof typeof Env]);

const isGithubCI = process.env.GITHUB_ACTIONS === 'true';
const isNextBuild = process.env.NEXT_PHASE === 'phase-production-build';

if (enableDatadog && !isGithubCI && !isNextBuild) {
  datadogRum.init({
    applicationId: Env.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
    clientToken: Env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.com',
    service: Env.NEXT_PUBLIC_DATADOG_SERVICE_NAME,
    env: Env.NEXT_PUBLIC_DATADOG_ENV,
    // Specify a version number to identify the deployed version of your application in Datadog
    // version: '1.0.0',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'allow',
    // Specify URLs to propagate trace headers for connection between RUM and backend trace
    allowedTracingUrls: [
      { match: '/api/*', propagatorTypes: ['tracecontext'] },
    ],
  });
}

export default function DatadogInit() {
  // Render nothing - this component is only included so that the init code
  // above will run client-side
  const pathname = usePathname();

  useEffect(() => {
    if (!enableDatadog) {
      console.warn('Missing required environment variables for Datadog.');
    }
  }, []);

  useEffect(() => {
    if (enableDatadog) {
      const getUser = async () => {
        const user = await getClientUser();

        if (user) {
          datadogRum.setUser({
            id: user.id,
            email: user.email,
            name: user.user_metadata.name,
          });
        }
      };

      getUser();
    }
  }, [pathname]);

  return null;
}
